import { ref } from 'vue'
import { updateMySettings, type ListViewPrefs } from '@/api'
import { reportError } from '@/errorReport'

const LEGACY_KEY = 'todos.listView' // (the browser-only copy from before the view was kept in the user's settings)
const SAVE_DELAY = 600

// The todo list page's last status, sort, grouping and folded groups, kept in the user's settings (`users.list_view`) so
// they follow the user across devices. Module level like `useTagEditor`: `App.vue` hands over the loaded settings once
// (`initListViewPrefs`), the list page waits for `listViewReady`, applies `listViewPrefs` and reports changes with `saveListView`.
export const listViewPrefs = ref<ListViewPrefs | null>(null)

let markReady: () => void = () => {}
export const listViewReady: Promise<void> = new Promise((resolve) => {
  markReady = resolve
})

let timer: ReturnType<typeof setTimeout> | undefined
let pending: ListViewPrefs | null = null

function readLegacy(): ListViewPrefs | null {
  try {
    const raw = localStorage.getItem(LEGACY_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<{ status: string; sort: string; group: string; collapsedGroups: string[] }>
    return {
      status: parsed.status === 'all' || parsed.status === 'done' ? parsed.status : 'open',
      sort: ['due', 'priority', 'created', 'title'].includes(parsed.sort ?? '') ? (parsed.sort as ListViewPrefs['sort']) : 'manual',
      group: parsed.group === 'tag' ? 'tag' : 'none',
      collapsed_groups: Array.isArray(parsed.collapsedGroups) ? parsed.collapsedGroups.slice(0, 200) : [],
    }
  } catch {
    return null
  }
}

function dropLegacy(): void {
  try {
    localStorage.removeItem(LEGACY_KEY)
  } catch {
    // storage unavailable -- nothing to remove
  }
}

async function persist(view: ListViewPrefs): Promise<void> {
  try {
    await updateMySettings({ list_view: view })
    dropLegacy()
  } catch (e) {
    reportError(e, 'listView.saveFailed') // (the view just won't stick; no toast for a background save)
  }
}

// Called once with the `list_view` of the loaded settings (`null` when none is stored, or when the settings failed to load).
// A view only stored by the browser is adopted and sent to the server once.
export function initListViewPrefs(server: ListViewPrefs | null, loaded = true): void {
  let value = server
  if (loaded && !value) {
    const legacy = readLegacy()
    if (legacy) {
      value = legacy
      void persist(legacy)
    }
  } else if (loaded) {
    dropLegacy()
  }
  listViewPrefs.value = value
  markReady()
}

// Remembers the view after a pause; an unchanged one is not sent.
export function saveListView(view: ListViewPrefs): void {
  if (JSON.stringify(view) === JSON.stringify(listViewPrefs.value)) return
  listViewPrefs.value = view
  pending = view
  clearTimeout(timer)
  timer = setTimeout(() => {
    const next = pending
    pending = null
    if (next) void persist(next)
  }, SAVE_DELAY)
}

// "Clear saved filters": forget the stored view, the list page starts from its defaults again.
export async function clearListView(): Promise<void> {
  clearTimeout(timer)
  pending = null
  await updateMySettings({ clear_list_view: true })
  listViewPrefs.value = null
  dropLegacy()
}
